/**
 * ExplanationQuiz Adult Renderer
 * 서술형 문제 - textarea로 자유 텍스트 입력, 채점 없음
 */

import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import KatexRenderer from '../../../utils/KatexRenderer'
import { formatProblem } from '../../../utils/quiz-parser'
import { QuizResultFeedback, SubmitButton, QuizHeader, QuizSolveDescription } from '../shared'
import type { BaseQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { useExplanationQuiz } from '../hooks'

interface ExplanationQuizProps {
  quizId: string | number
  quiz: BaseQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** true면 풀이 후 해설을 숨긴다. */
  hideExplanation?: boolean
}

export function ExplanationQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: ExplanationQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    answer,
    setAnswer,
    isSolved,
    canSubmit,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit,
  } = useExplanationQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (!quiz.problem) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem`,
      () => null,
      quiz.additionalImages ?? undefined,
    )
  }, [quiz.problem, quiz.additionalImages, quizId])

  const correctAnswer = solveResult?.correctAnswers?.join(', ') ?? ''
  const displayedAnswer = isViewSolved && correctAnswer ? correctAnswer : answer

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.explanation.label', '서술형')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
      />

      {/* 문제 본문 — 모범답안 마커({{...}})는 textarea와 중복되므로 제외 */}
      {renderedProblem.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          data-quiz-problem-text
          className={cn(
            'p-4 rounded-xl bg-gray-100',
            'text-xl text-gray-700',
            '[&_.formatProblem]:leading-relaxed',
          )}
        >
          {renderedProblem}
        </motion.div>
      )}

      {/* 답변 입력 textarea */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        {isSolved || isViewSolved ? (
          <div
            data-testid="explanation-answer"
            className="min-h-[180px] w-full p-4 rounded-xl border-2 border-gray-200 bg-gray-50 text-lg text-gray-700 whitespace-pre-wrap break-words"
          >
            <KatexRenderer text={displayedAnswer} />
          </div>
        ) : (
          <textarea
            value={displayedAnswer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={t('types.explanation.placeholder', '답변을 입력하세요')}
            maxLength={500}
            rows={5}
            className={cn(
              'w-full p-4 rounded-xl border-2 text-lg text-gray-700 resize-y transition-colors outline-hidden',
              'bg-white border-gray-300 focus:border-blue-400 focus:ring-2 focus:ring-blue-100'
            )}
          />
        )}
        <div className="mt-1 text-right text-sm text-gray-400">
          {displayedAnswer.length}/500
        </div>
      </motion.div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? null}
          show={isSolved}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 모범답안 표시 */}
      {!isViewSolved && isSolved && solveResult?.correctAnswers && solveResult.correctAnswers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-blue-50 border border-blue-200"
        >
          <button
            onClick={() => setIsShowingCorrectAnswers(v => !v)}
            className="text-sm font-semibold text-blue-700 hover:text-blue-800"
          >
            {isShowingCorrectAnswers ? t('common.viewMyAnswer', '내 답안') : t('common.viewCorrectAnswer', '모범답안 보기')}
          </button>
          {isShowingCorrectAnswers && (
            <div className="mt-2 text-base text-blue-900">
              <KatexRenderer text={solveResult.correctAnswers.join(', ')} />
            </div>
          )}
        </motion.div>
      )}

      {/* 해설 — 다른 adult 문제 유형과 동일하게 검수/풀이 완료 시 노출 */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={false}
        />
      )}
    </div>
  )
}
